import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Result } from 'antd';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { useToken } from '@/common/helper';
import type { RootState } from '@/redux-toolkit/store';
import HomeSingleShipmentData from '../../Home/map/components/HomeSingleShipmentData';
import {
  filterHazardsByVisibility,
  getNextHazardBoundaryMs,
} from '../../Home/map/hazardTypes';
import {
  inheritHazardPresentation,
  type HazardAlertLike,
} from '../../Home/map/hazards/hazardPresentation';
import { findLaneEvents, type LaneEvent } from '../../Home/map/laneEvents';
import { shipmentEndpoints } from '../../Home/map/monitorMapEffects';
import { toPadding } from '../../Home/map/monitorRouteShape';
import type { MonitorTripLine } from '../monitorTripLine';
import {
  placeShipment,
  shipmentPosition,
  type ShipmentPlace,
  type StreamShipment,
} from '../../Home/map/needsAttention';
import {
  GlobeMonitorController,
  type GlobeAirport,
} from './GlobeMonitorController';
import { useFocusedShipment } from './useFocusedShipment';
import {
  useMonitorAirports,
  type MonitorAirportContext,
} from './useMonitorAirports';
import { useMonitorShipmentStream } from './useMonitorShipmentStream';

type PaddingLike = { top: number; right: number; bottom: number; left: number };

export interface GlobeMonitorMapProps {
  newsFlashPolygon: unknown;
  /** null: overview; an id: that shipment's spotlight. */
  focusShipmentId: string | null;
  /** The trip panel could not load: the card shows where and when. */
  tripUnavailable?: boolean;
  /** The spotlight's line (see monitorTripLine); null: none (yet). */
  focusRoute?: MonitorTripLine | null;
  /**
   * Where the spotlight shipment is now, from its trip (see MonitorTrip.now):
   * its marker, circle and line use this instead of the stream's position.
   */
  focusPlace?: ShipmentPlace | null;
  onShipmentsChange: (shipments: StreamShipment[]) => void;
  /** Keeps the overview clear of the rail and arrivals board. */
  fitBoundsPadding: PaddingLike;
  /** Keeps a spotlight clear of the card, rail and trip panel. */
  focusFitPadding: PaddingLike;
  /** The screen's teams ([] = everything the user can see). */
  teamIds?: string[];
  onLaneEventsChange?: (events: LaneEvent[]) => void;
  onActiveCountChange?: (count: number | null) => void;
  /** Airport delays, for the attention ranking (as the flat map reports). */
  onAirportContextChange?: (context: MonitorAirportContext | null) => void;
  /** The delayed airports to draw: near the shipments on screen. */
  visibleAirportIcaos?: string[];
}

/** Event areas the lane check considers: every visible one (as on the map). */
const laneHazards = (newsFlashPolygon: unknown): HazardAlertLike[] =>
  filterHazardsByVisibility(
    (
      (Array.isArray(newsFlashPolygon)
        ? newsFlashPolygon
        : []) as (HazardAlertLike & {
        polygons?: HazardAlertLike[];
      })[]
    ).flatMap((item) =>
      Array.isArray(item?.polygons)
        ? item.polygons.map((polygon: HazardAlertLike) =>
            inheritHazardPresentation(item, polygon)
          )
        : []
    ),
    { showWeatherLayer: true, showGeopoliticalLayer: true }
  );

/**
 * /monitor's map: a globe (Mapbox globe projection) with the shipment
 * stream, attention rules and spotlight, drawn by GlobeMonitorController.
 */
const GlobeMonitorMap = ({
  newsFlashPolygon,
  focusShipmentId,
  tripUnavailable = false,
  focusRoute = null,
  focusPlace = null,
  onShipmentsChange,
  fitBoundsPadding,
  focusFitPadding,
  teamIds,
  onLaneEventsChange,
  onActiveCountChange,
  onAirportContextChange,
  visibleAirportIcaos,
}: GlobeMonitorMapProps) => {
  const { token, decodedUserToken } = useToken();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<GlobeMonitorController | null>(null);
  const [controllerKey, setControllerKey] = useState(0);
  const [mapFailed, setMapFailed] = useState(false);
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

  const {
    shipments: streamed,
    activeCount,
    snapshots,
  } = useMonitorShipmentStream(token, teamIds ?? []);
  const shipments = useMemo(
    () => placeShipment(streamed, focusPlace),
    [streamed, focusPlace]
  );
  const shipmentsRef = useRef(shipments);
  shipmentsRef.current = shipments;
  const focused = useFocusedShipment(token, focusShipmentId);
  const airports = useMonitorAirports(token, decodedUserToken()?.company_id);

  // One map per theme: a theme change rebuilds it, like the flat monitor.
  // A failed map is shut down (the cleanup) and not rebuilt.
  useEffect(() => {
    if (mapFailed) return undefined;
    if (!containerRef.current || !accessToken.startsWith('pk.')) {
      setMapFailed(true);
      return undefined;
    }
    mapboxgl.accessToken = accessToken;
    const controller = new GlobeMonitorController(
      containerRef.current,
      { dark: darkModeEnabled },
      () => setMapFailed(true)
    );
    controllerRef.current = controller;
    setControllerKey((key) => key + 1);
    return () => {
      controller.destroy();
      controllerRef.current = null;
    };
  }, [darkModeEnabled, accessToken, mapFailed]);

  useEffect(() => {
    // The page gets the stream's own positions: it ranks what needs
    // attention from them, and a spotlight's placement must not re-rank its
    // own rotation. The globe draws the placed ones.
    onShipmentsChange(streamed);
  }, [streamed, onShipmentsChange, controllerKey]);

  useEffect(() => {
    controllerRef.current?.setShipments(shipments);
  }, [shipments, controllerKey]);

  useEffect(() => {
    onActiveCountChange?.(activeCount);
  }, [activeCount, onActiveCountChange]);

  useEffect(() => {
    onAirportContextChange?.(airports);
  }, [airports, onAirportContextChange]);

  // Redrawn only when the set or a delay changes, not on every new array.
  const airportMarkers = useMemo((): GlobeAirport[] => {
    const meta = airports?.airportMeta ?? {};
    return (visibleAirportIcaos ?? []).flatMap((icao) => {
      const airport = meta[icao];
      if (typeof airport?.lat !== 'number' || typeof airport?.lng !== 'number')
        return [];
      return [
        {
          icao,
          code: airport.iata || icao,
          lat: airport.lat,
          lng: airport.lng,
          delayPct: airport.delayStats?.delayed_cancelled_pct ?? 0,
        },
      ];
    });
  }, [airports, visibleAirportIcaos]);
  const airportKey = airportMarkers
    .map((a) => `${a.icao}:${Math.round(a.delayPct)}`)
    .join(',');
  useEffect(() => {
    controllerRef.current?.setAirports(airportMarkers);
    // airportKey stands for airportMarkers' content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportKey, controllerKey]);

  // One clock for the event areas and "On this route" (as on the flat map):
  // it ticks when the data changes and at the next start or expiry.
  const [hazardClockMs, setHazardClockMs] = useState(() => Date.now());
  useEffect(() => {
    setHazardClockMs(Date.now());
  }, [newsFlashPolygon]);
  useEffect(() => {
    const next = getNextHazardBoundaryMs(
      Array.isArray(newsFlashPolygon) ? newsFlashPolygon : [],
      hazardClockMs
    );
    if (next == null) return undefined;
    const id = window.setTimeout(
      () => setHazardClockMs(Date.now()),
      Math.min(2_147_483_647, Math.max(0, next - hazardClockMs + 25))
    );
    return () => window.clearTimeout(id);
  }, [hazardClockMs, newsFlashPolygon]);

  useEffect(() => {
    controllerRef.current?.setHazards(newsFlashPolygon, hazardClockMs);
  }, [newsFlashPolygon, hazardClockMs, controllerKey]);

  // Camera and focus: on every step change (and map rebuild).
  const showOverview = () => {
    const points = shipmentsRef.current
      .map(shipmentPosition)
      .filter((p): p is { lat: number; lng: number } => p !== null);
    controllerRef.current?.showOverview(points, toPadding(fitBoundsPadding));
  };
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    controller.setFocusId(focusShipmentId);
    controller.setRoute(null);
    controller.setEndpoints([]);
    if (!focusShipmentId) {
      controller.setFocusRing(null, null, null);
      controller.setLaneKeys([]);
      showOverview();
      return;
    }
    const target = shipmentsRef.current.find(
      (s) => String(s.shipment_id) === focusShipmentId
    );
    const position = shipmentPosition(target);
    controller.setFocusRing(target ?? null, position, null);
    if (position) controller.focusOn(position, toPadding(focusFitPadding));
    // Paddings are read once per step; a later change only reframes routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusShipmentId, controllerKey]);

  // The first overview runs before any shipment has arrived: face the fleet
  // once it has, and again after every complete snapshot, since the fleet
  // arrives in batches and changes over time (an all-clear screen never
  // leaves the overview). Only on the overview, never mid-spotlight. A
  // snapshot that empties the fleet turns it back to the default globe.
  const hasShipments = shipments.length > 0;
  useEffect(() => {
    if (!focusShipmentId && (hasShipments || snapshots > 0)) showOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasShipments, snapshots]);

  // The spotlight's line, pins, arrow and events once its trip is in.
  const detail = focused?.detail ?? null;
  const route =
    focusShipmentId && focusRoute?.key === focusShipmentId ? focusRoute : null;

  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !route) return;
    // Keyed on the shipment: re-runs for it continue its draw-in.
    controller.setRoute(
      {
        traveled: route.traveled,
        remaining: route.remaining,
        moving: route.moving,
      },
      route.key
    );
    // The whole line and the shipment (see MonitorTripLine.frame).
    if (route.frame.length >= 2)
      controller.frameRoute(route.frame, toPadding(focusFitPadding));
    // focusFitPadding: reframe when the trip panel grows.
  }, [route, focusFitPadding, controllerKey]);

  // The ring stays on the shipment in focus as new positions arrive, with or
  // without a line (no origin or destination: no line); the line only adds
  // the arrow.
  const focusTarget = focusShipmentId
    ? shipments.find((s) => String(s.shipment_id) === focusShipmentId)
    : undefined;
  const focusPosition = shipmentPosition(focusTarget);
  const focusLat = focusPosition?.lat;
  const focusLng = focusPosition?.lng;
  const heading = route?.heading ?? null;
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !focusShipmentId) return;
    const target = shipmentsRef.current.find(
      (s) => String(s.shipment_id) === focusShipmentId
    );
    controller.setFocusRing(target ?? null, shipmentPosition(target), heading);
  }, [focusShipmentId, focusLat, focusLng, heading, controllerKey]);

  // The trip placed the shipment somewhere else (focusPlace) and there is no
  // line to frame it (origin or destination unknown): turn to it, or the
  // camera stays on the stream's stale position.
  const placeId = focusPlace?.shipmentId;
  const placeLat = focusPlace?.lat;
  const placeLng = focusPlace?.lng;
  const hasRoute = route !== null;
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !focusShipmentId || hasRoute) return;
    if (placeId !== focusShipmentId) return;
    if (placeLat === undefined || placeLng === undefined) return;
    controller.focusOn(
      { lat: placeLat, lng: placeLng },
      toPadding(focusFitPadding)
    );
    // Paddings are read when it moves; a later change only reframes routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusShipmentId, hasRoute, placeId, placeLat, placeLng, controllerKey]);

  useEffect(() => {
    controllerRef.current?.setEndpoints(
      detail ? shipmentEndpoints(detail) : []
    );
  }, [detail, controllerKey]);

  const laneEvents = useMemo(() => {
    if (!route) return [];
    const eta = Date.parse(detail?.estimated_delivery_date ?? '');
    return findLaneEvents({
      hazards: laneHazards(newsFlashPolygon),
      route: route.lane,
      nowMs: hazardClockMs,
      arrivalMs: Number.isFinite(eta) ? eta : null,
      startsAtShipment: route.moving,
    });
  }, [route, detail, newsFlashPolygon, hazardClockMs]);

  useEffect(() => {
    onLaneEventsChange?.(laneEvents);
    controllerRef.current?.setLaneKeys(laneEvents.map((event) => event.key));
  }, [laneEvents, onLaneEventsChange, controllerKey]);

  if (mapFailed) {
    return (
      <Result
        status='warning'
        title='The globe could not load'
        subTitle='Check the Mapbox token and network, then reload.'
      />
    );
  }

  return (
    <div className='position-relative h-100 monitor-globe'>
      <div ref={containerRef} className='monitor-globe-canvas' />
      {focusShipmentId && detail && (
        <HomeSingleShipmentData
          variant='panel'
          compact
          showTripFallback={tripUnavailable}
          shipmentData={detail}
          alertsData={focused?.alerts ?? undefined}
          closeModal={() => undefined}
          goToShipmentDetail={() => undefined}
        />
      )}
    </div>
  );
};

export default GlobeMonitorMap;
